<template>
  <div class="c-combo">
    <el-form ref="formRef" :disabled="disabled" v-bind="innerFormAttrs" :model="comboData">
      <div v-if="!dataProcessing" class="combo__body">
        <div v-for="(it, index) in comboData.data" :key="it.__id || index" class="section-con">
          <combo-section
            class="section-item"
            :model="comboData"
            :model-index="index"
            :items="formItems"
            :items-attrs="itemsAttrs"
            v-bind="comboSectionAttrs"
          >
            <template #header="scope">
              <div class="section-header" :style="header?.style" :class="header?.class">
                <slot name="sectionHeader" v-bind="scope">
                  <Content v-if="header?.content" :content="header?.content" :context-data="scope" />
                </slot>
              </div>
            </template>
            <template #footer="scope">
              <div class="section-footer" :style="footer?.style" :class="footer?.class">
                <slot v-if="!noOperation" name="operation" v-bind="scope">
                  <Content v-if="footer?.content" :content="footer?.content" :context-data="scope" />
                </slot>
              </div>
            </template>
          </combo-section>
        </div>
      </div>
    </el-form>
  </div>
</template>

<script lang="ts">
export default { inheritAttrs: false }
</script>

<script setup lang="ts">
import {
  _,
  uniqId,
  ref,
  reactive,
  nextTick,
  computed,
  inject,
  onMounted,
  onUnmounted,
  useCurrentAppInstance
} from '@zto/zpage'
import { C_FORM_KEY } from '@zto/zpage-ui-element'

import ComboSection from './combo-section.vue'

import type { GenericFunction, ApiRequestAction, AppEventListeners } from '@zto/zpage'
import type { ComboData, ComboFormItem } from './types'

// 属性
const props = withDefaults(
  defineProps<{
    api?: ApiRequestAction // api请求
    apiParams?: Record<string, any> // api请求参数

    data?: Array<any> // 数据
    dataProp?: string // 数据属性
    onDataLoad?: GenericFunction // 数据加载

    items?: Array<any> // 表单项设置
    itemsAttrs?: any // 表单项属性
    formAttrs?: any // 表单属性
    sectionAttrs?: any // 快属性
    labelWidth?: number // label宽度
    border?: boolean
    direction?: string

    header?: Record<string, any> // 头部属性
    footer?: Record<string, any> // 头部属性

    noIndex?: boolean // 是否展示序号列
    noOperation?: boolean // 是否展示操作列
    operationPosition?: string // 操作列位置

    editable?: boolean // 是否可编辑
    disabled?: boolean // 是否disable编辑
    dataDefaults?: any // 编辑器默认值

    listeners?: AppEventListeners // 时间监听配置
  }>(),
  {
    data: () => [],
    items: () => [],
    noIndex: false,
    noOperation: false,

    labelWidth: 100,

    border: true,
    editable: false
  }
)

// 事件
const emit = defineEmits(['fetch'])

const app = useCurrentAppInstance()

const dataLoading = ref(false)
const dataProcessing = ref(false)

const comboData = reactive<ComboData>({
  data: []
})

const comboSectionAttrs = computed(() => {
  return {
    direction: props.direction,
    border: props.border !== false,
    ...props.sectionAttrs
  }
})

const formItems = computed<ComboFormItem[]>(() => {
  const formItems = _normalizeFormItems(props.items)
  return formItems
})

/** 规范化列配置 */
function _normalizeFormItems(items: ComboFormItem[]) {
  if (!items?.length) return [...(items || [])]

  const _items = items
    .filter(it => {
      return app.checkPermission(it.perm)
    })
    .map((it: any, index: number) => {
      const _it = { ...it }
      _it.__id = `${index}_${uniqId()}`
      _it.editorProp = _it.editor?.prop || _it.prop // 设置编辑属性
      if (!_it.prop) _it.prop = _it.__id // 如果没有prop，默认设置prop，以备后续识别
      return _it
    })

  return _items
}

//#region 操作相关

// 注册微件事件监听
app.useEventListeners(props.listeners, {
  fetchOn: async (e: any) => {
    await doFetch({ apiParams: e?.params })
  },

  setDataOn: async (e: any) => {
    setData(e.data)
  }
})

//#endregion

//#region 数据相关

/** 请求数据 */
async function doFetch(options?: any) {
  if (dataLoading.value) return

  options = { ...options }

  const payload = {
    action: { type: 'query', api: props.api, sourceType: 'combo' },
    params: { ...props.apiParams, ...options.apiParams }
  }

  if (props.api) {
    dataLoading.value = true
    await app
      .request(payload)
      .then(res => {
        setData(res)
      })
      .finally(() => {
        dataLoading.value = false
      })
  }

  emit('fetch', payload)
}

/** 设置表格数据 */
async function setData(data: any) {
  if (!data) return

  const dataProp = props.dataProp || 'data'

  let list: any[] = []

  if (Array.isArray(data)) {
    list = data
  } else if (dataProp) {
    list = data[dataProp]
  }

  await nextTick(async () => {
    comboData.data = list || []

    if (props.onDataLoad) {
      const context = app.useContext(comboData)
      await props.onDataLoad(context)
    }
  })
}

// 返回组合数据
function getData() {
  return comboData.data
}

//#endregion

//#region 表格编辑相关

const exFormRules = app.useComponentsConfig('form.rules')

const formRef = ref<any>()

// 外部表单
const outerForm = inject<any>(C_FORM_KEY)

// 当前可编辑表格对应外部表格
const outerFormField = { validate }

// 内部属性
const innerFormAttrs = computed(() => {
  return {
    labelWidth: props.labelWidth,
    ...props.formAttrs
  }
})

/** 默认数据 */
const formItemDataDefaults = computed(() => {
  const data = formItems.value.reduce((acc, it) => {
    if (it.prop && it.hasOwnProperty('default')) {
      acc[it.prop] = it.default
    }
    return acc
  }, {} as any)

  return data
})

onMounted(() => {
  if (outerForm) {
    outerForm.addValidatorItem(outerFormField)
  }
})

onUnmounted(() => {
  if (outerForm) {
    outerForm.removeValidatorItem(outerFormField)
  }
})

/** 验证 */
function validate() {
  return formRef.value.validate()
}

/** 附加数据 */
function appendItem(data: any) {
  data = getItemDataWithDefaults(data)
  comboData.data.push(data)
}

/** 附加多行 */
function appendItems(datas: any[] = [{}]) {
  datas = getItemDatasWithDefaults(datas)
  comboData.data.push(...datas)
}

/** 插入行 */
function insertItem(index = 0, data?: any) {
  data = getItemDataWithDefaults(data)
  comboData.data.splice(index + 1, 0, data)
}

/** 插入多行 */
function insertItems(index = 0, datas: any[] = [{}]) {
  datas = getItemDatasWithDefaults(datas)
  comboData.data.splice(index + 1, 0, ...datas)
}

/** 获取行默认数据 */
function getItemDatasWithDefaults(datas: any[] = [{}]) {
  datas = datas.reduce((acc, cur) => {
    const data = _.deepMerge(props.dataDefaults, formItemDataDefaults.value, cur)
    acc.push(data)
    return acc
  }, [] as any[])

  return datas
}

/** 获取行默认数据 */
function getItemDataWithDefaults(data: any = {}) {
  data = _.deepMerge(props.dataDefaults, formItemDataDefaults.value, data)

  return data
}

/** 删除行 */
async function removeItem(index = 0) {
  dataProcessing.value = true

  comboData.data.splice(index, 1)

  await _.delay(1)
  dataProcessing.value = false
}

/** 删除多行 */
async function removeItems(rows: any[]) {
  if (!rows || !rows.length) return

  dataProcessing.value = true

  let list = comboData.data

  for (let i = 0; i < list.length; i++) {
    if (rows.includes(list[i])) {
      list.splice(i, 1)
      i--
    }
  }

  await _.delay(1)
  dataProcessing.value = false
}

/**
 * 设置行数据
 * @param datas
 * @param keyProp
 * @param ignoreProps
 */
function setItemsDatasByKey(datas: any[], keyProp: string, setFn?: Function) {
  let rows = comboData.data
  keyProp = keyProp || 'code'

  _.setObjectsByKeys(rows, datas, keyProp, setFn)
}

/**
 * 为数据添加不存在的key
 */
function setItemsDefaults(rows: any[]) {
  const _formItems = formItems.value || []

  if (!rows?.length) return

  rows.forEach((item: any) => {
    let existsProps = Object.keys(item)

    _formItems.forEach(it => {
      if (it.editorProp && !existsProps.includes(it.editorProp)) {
        if (it.hasOwnProperty('default')) {
          item[it.editorProp] = it.default
        }
      }
    })
  })
}

//#endregion

/** 导出组件方法 */
defineExpose({
  outerForm,
  innerForm: formRef,
  comboData,
  setData,
  getData,
  doFetch,
  validate,
  appendItem,
  appendItems,
  insertItem,
  insertItems,
  removeItem,
  removeItems,
  setItemsDatasByKey,
  setItemsDefaults
})
</script>

<style lang="scss" scoped>
.combo__body {
  & > .section-con {
    padding-bottom: 10px;
  }
}
</style>
